import { Shield } from "lucide-react";
import { PLAN_TYPE_OPTIONS } from "@shared/schema";
import { SIC_CODES, formatSicCode } from "@shared/sicCodes";
import type { StepDefinition } from "./types";

// Step definitions for new quote process (full flow with plan selection)
// Note: RFP steps removed - Quote mode goes directly from Plan Selection to Quote Summary
export const QUOTE_STEPS: StepDefinition[] = [
  { id: 1, title: "Select Client/Prospect", description: "Choose who to quote" },
  { id: 2, title: "Coverage Types", description: "Select coverage types" },
  { id: 3, title: "Review Census", description: "Select employees to quote" },
  { id: 4, title: "Select Carriers", description: "Choose carriers for each coverage" },
  { id: 5, title: "Plan Selection", description: "Select specific plans" },
  { id: 6, title: "Quote Summary", description: "Review selected plans" },
  { id: 7, title: "Generate Quote", description: "Generate final quote" },
];

// Step definitions for RFP process (skips plan selection - used for soliciting proposals)
export const RFP_STEPS: StepDefinition[] = [
  { id: 1, title: "Select Client/Prospect", description: "Choose who to send RFP" },
  { id: 2, title: "Coverage Types", description: "Select coverage types" },
  { id: 3, title: "Review Census", description: "Select employees for census" },
  { id: 4, title: "Recipients", description: "Who receives this RFP" },
  { id: 5, title: "RFP Parameters", description: "Set RFP requirements" },
  { id: 6, title: "RFP Preview", description: "Review and send RFP" },
];

// Legacy export for backward compatibility
export const STEPS = QUOTE_STEPS;

// Coverage types sourced from shared plan enums (for Quote mode)
export const COVERAGE_TYPES = PLAN_TYPE_OPTIONS
  .filter((opt) => opt.value !== "medical") // Remove 'medical' since we keep 'health'
  .filter((opt) => opt.value !== "disability") // Remove Disability Insurance
  .filter((opt) => opt.value !== "voluntary") // Remove Voluntary Benefits
  .map((opt) => ({
    id: opt.value,
    label: opt.label,
    icon: Shield,
  }));

// Coverage types for RFP mode (includes Disability and Voluntary Benefits)
export const RFP_COVERAGE_TYPES = PLAN_TYPE_OPTIONS
  .filter((opt) => opt.value !== "medical") // Remove 'medical' since we keep 'health'
  .map((opt) => ({
    id: opt.value,
    label: opt.label,
    icon: Shield,
  }));

export const getCoverageLabel = (value: string): string =>
  PLAN_TYPE_OPTIONS.find((o) => o.value === value)?.label ||
  value.charAt(0).toUpperCase() + value.slice(1);

export const getSicCodeDisplay = (
  sicCodeId?: number | null,
  legacySic?: string | null
): string => {
  if (sicCodeId) {
    const sicCode = SIC_CODES.find((sic) => sic.code === sicCodeId);
    if (sicCode) {
      return `${formatSicCode(sicCode.code)} - ${sicCode.industryTitle}`;
    }
  }
  return legacySic || "N/A";
};
